import { Viewer } from "cesium";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { BackgroundableToolInterface } from "./BackgroundableToolInterface";
import {
  SerializableToolInterface,
  compareToolState,
} from "./SerializableToolInterface";
import TerrainPlugin from "./TerrainPlugin";
import {
  ObservableProperty,
  DerivedObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";

export type GlobeOpacityToolSnapshot =
  | GlobeOpacityToolSnapshotV1
  | GlobeOpacityToolSnapshotV2;
type GlobeOpacityToolSnapshotV1 = {
  version?: 1;
  globeOpacity: number;
};
type GlobeOpacityToolSnapshotV2 = {
  version: 2;
  globeOpacity: number;
  backgroundColor: string;
  terrainEnabled: boolean;
};

function migrateState(
  state: GlobeOpacityToolSnapshot | undefined,
): GlobeOpacityToolSnapshotV2 | undefined {
  if (!state || state.version === 2) return state as GlobeOpacityToolSnapshotV2;
  return {
    version: 2,
    globeOpacity: state.globeOpacity,
    backgroundColor:
      (state as unknown as GlobeOpacityToolSnapshotV2).backgroundColor ??
      DEFAULT_BACKGROUND_COLOR,
    terrainEnabled:
      (state as unknown as GlobeOpacityToolSnapshotV2).terrainEnabled ?? true,
  };
}

export function isGlobeOpacityStateApplied(
  state: GlobeOpacityToolSnapshot | undefined,
): boolean {
  state = migrateState(state);
  return (
    state &&
    ((state.globeOpacity ?? 1) < 0.999 || state.terrainEnabled === false)
  );
}

const DEFAULT_GLOBE_OPACITY = 1;
const DEFAULT_UNDERGROUND_OPACITY = 0.2;
const DEFAULT_BACKGROUND_COLOR = "#000000";

export default class GlobeOpacityToolPlugin
  implements
    AttachablePluginInterface,
    SerializableToolInterface<GlobeOpacityToolSnapshot>,
    BackgroundableToolInterface
{
  private _overgroundOpacity = DEFAULT_GLOBE_OPACITY;
  private _undergroundOpacity = DEFAULT_UNDERGROUND_OPACITY;
  private _previousUndergroundState = false;
  private _removeUndergroundMonitor: (() => void) | null = null;

  private readonly _globeOpacity = new ObservableProperty<number>(
    DEFAULT_GLOBE_OPACITY,
  );
  private readonly _isUnderground = new ObservableProperty<boolean>(false);
  private readonly _backgroundColor = new ObservableProperty<string>(
    DEFAULT_BACKGROUND_COLOR,
  );
  private readonly _terrainEnabled = new ObservableProperty<boolean>(true);
  readonly isApplied = new DerivedObservableProperty(
    () => isGlobeOpacityStateApplied(this.capture()),
    [this._globeOpacity, this._terrainEnabled],
  );

  constructor(private readonly _terrain: TerrainPlugin) {}

  get globeOpacity(): ReadonlyObservableProperty<number> {
    return this._globeOpacity;
  }

  get isUnderground(): ReadonlyObservableProperty<boolean> {
    return this._isUnderground;
  }

  get backgroundColor(): ReadonlyObservableProperty<string> {
    return this._backgroundColor;
  }

  get terrainEnabled(): ReadonlyObservableProperty<boolean> {
    return this._terrainEnabled;
  }

  setViewer(viewer: Viewer) {
    // Sync initial opacity if viewer already has translucency set
    const initialAlpha = viewer.scene.globe.translucency.frontFaceAlpha;
    if (initialAlpha < 0.999) {
      this._globeOpacity.set(initialAlpha);
      this._overgroundOpacity = initialAlpha;
    }

    const checkUnderground = () => {
      const isNowUnderground = this._terrain.isCameraUnderground;
      if (isNowUnderground === this._previousUndergroundState) return;
      this._previousUndergroundState = isNowUnderground;

      if (isNowUnderground) {
        this._overgroundOpacity = this._globeOpacity.getSnapshot();
        this._isUnderground.set(true);
        this.setGlobeOpacity(this._undergroundOpacity);
      } else {
        this._isUnderground.set(false);
        this.setGlobeOpacity(this._overgroundOpacity);
      }
    };

    this._removeUndergroundMonitor =
      this._terrain.onPostRender(checkUnderground);
  }

  detachViewer() {
    this._removeUndergroundMonitor?.();
    this._removeUndergroundMonitor = null;
  }

  clear(): void {
    this.setGlobeOpacity(DEFAULT_GLOBE_OPACITY);
    this.setBackgroundColor(DEFAULT_BACKGROUND_COLOR);
    this.setTerrainEnabled(true);
    this._undergroundOpacity = DEFAULT_UNDERGROUND_OPACITY;
  }

  capture(): GlobeOpacityToolSnapshot {
    return {
      version: 2,
      globeOpacity: this._globeOpacity.getSnapshot(),
      backgroundColor: this._backgroundColor.getSnapshot(),
      terrainEnabled: this._terrainEnabled.getSnapshot(),
    };
  }

  restore(state: GlobeOpacityToolSnapshot | undefined): void {
    if (!state) return;
    state = migrateState(state);
    this.setGlobeOpacity(state.globeOpacity);
    this.setBackgroundColor(state.backgroundColor);
    this.setTerrainEnabled(state.terrainEnabled);
  }

  isStateApplied(state: GlobeOpacityToolSnapshot | undefined): boolean {
    return isGlobeOpacityStateApplied(state);
  }

  isMatchingState(state: GlobeOpacityToolSnapshot | undefined): boolean {
    state = migrateState(state);
    return compareToolState(this.capture(), state, (s) =>
      this.isStateApplied(s),
    );
  }

  setGlobeOpacity(opacity: number) {
    if (Number.isNaN(opacity)) opacity = DEFAULT_GLOBE_OPACITY;
    this._globeOpacity.set(opacity);

    if (this._isUnderground.getSnapshot()) {
      this._undergroundOpacity = opacity;
    } else {
      this._overgroundOpacity = opacity;
    }

    this._terrain.setOpacity(opacity);
  }

  setBackgroundColor(color: string) {
    this._backgroundColor.set(color);
    if (this._terrainEnabled.getSnapshot()) {
      this._terrain.setUndergroundColor(color);
    } else {
      this._terrain.setSceneColor(color);
    }
  }

  setTerrainEnabled(enabled: boolean) {
    this._terrainEnabled.set(enabled);
    this._terrain.toggleTerrain(enabled);
  }
}
